import React, { useState, useEffect, useCallback } from 'react';
import { IoMdArrowDropright } from 'react-icons/io';
import { FaSquare, FaCheckSquare, FaMinusSquare } from 'react-icons/fa';
import { AiOutlineLoading } from 'react-icons/ai';
import ApiRequestService from '../component/ApiRequestService';
import './qbtreeview.css';

/* ── Checkbox icon ──────────────────────────────────────────────── */
const CheckBox = ({ state, onClick }) => {
    const icon =
        state === 'all'  ? <FaCheckSquare /> :
        state === 'some' ? <FaMinusSquare /> :
                           <FaSquare />;
    return (
        <span
            className={`qb-tree-checkbox qb-tree-checkbox--${state}`}
            onClick={onClick}
        >
            {icon}
        </span>
    );
};

/* ── Expandable branch node (Subject / Topic) ──────────────────── */
const Branch = ({ label, level, levelClass, loadChildren, renderChildren }) => {
    const [expanded, setExpanded] = useState(false);
    const [children, setChildren] = useState(null);
    const [loading, setLoading] = useState(false);

    const toggle = async () => {
        if (!expanded && children === null) {
            setLoading(true);
            try {
                setChildren(await loadChildren());
            } finally {
                setLoading(false);
            }
        }
        setExpanded(prev => !prev);
    };

    return (
        <>
            <div className="qb-tree-node" data-level={level} onClick={toggle}>
                {loading ? (
                    <span className="qb-tree-loading"><AiOutlineLoading /></span>
                ) : (
                    <span className={`qb-tree-arrow ${expanded ? 'qb-tree-arrow--open' : 'qb-tree-arrow--closed'}`}>
                        <IoMdArrowDropright />
                    </span>
                )}
                <span className={`qb-tree-label ${levelClass}`}>{label}</span>
            </div>
            {expanded && children && renderChildren(children)}
        </>
    );
};

/* ── Category node (loads subcategories on mount, owns checkboxes) ── */
const CategoryNode = ({ subj, topic, cat, selected, onToggle, onToggleAll, onHydrate }) => {
    const [expanded, setExpanded] = useState(false);
    const [subcategories, setSubcategories] = useState(null);
    const [loading, setLoading] = useState(true);

    const buildMeta = (sc) => ({
        category_id: cat.category_id,
        category_name: cat.category_name,
        subcategory_id: sc.subcategory_id,
        subcategory_name: sc.subcategory_name,
    });

    const makeKey = (sc) =>
        `${subj.subject_id}_${topic.topic_id}_${cat.category_id}_${sc.subcategory_id}`;

    // Eagerly load subcategories so checkbox is visible immediately
    useEffect(() => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubCategories',
            data: {
                subject_id: subj.subject_id,
                topic_id: topic.topic_id,
                category_id: cat.category_id,
            },
        })
            .then(res => {
                const subs = res?.data?.data?.subCategory_names || [];
                setSubcategories(subs);
                // Hydrate any pre-selected keys that only have `true` (no metadata yet)
                onHydrate(subs.map(sc => ({ key: makeKey(sc), meta: buildMeta(sc) })));
            })
            .finally(() => setLoading(false));
    }, [subj.subject_id, topic.topic_id, cat.category_id]);

    // Derive category checkbox state from loaded subcategories
    let checkState = 'none';
    if (subcategories && subcategories.length > 0) {
        const count = subcategories.filter(sc => selected[makeKey(sc)]).length;
        if (count === subcategories.length) checkState = 'all';
        else if (count > 0) checkState = 'some';
    }

    return (
        <>
            <div className="qb-tree-node" data-level={3} onClick={() => setExpanded(prev => !prev)}>
                {loading ? (
                    <span className="qb-tree-loading"><AiOutlineLoading /></span>
                ) : (
                    <span className={`qb-tree-arrow ${expanded ? 'qb-tree-arrow--open' : 'qb-tree-arrow--closed'}`}>
                        <IoMdArrowDropright />
                    </span>
                )}
                {subcategories && (
                    <CheckBox
                        state={checkState}
                        onClick={e => {
                            e.stopPropagation();
                            onToggleAll(subcategories.map(sc => ({
                                key: makeKey(sc),
                                meta: buildMeta(sc),
                            })));
                        }}
                    />
                )}
                <span className="qb-tree-label qb-tree-label--category">
                    {cat.category_name}
                </span>
            </div>

            {expanded && subcategories?.map(sc => {
                const key = makeKey(sc);
                return (
                    <div key={sc.subcategory_id} className="qb-tree-node" data-level={4}>
                        {/* spacer matching the arrow width so subcategories indent under the category */}
                        <span className="qb-tree-arrow" style={{ visibility: 'hidden' }} />
                        <CheckBox
                            state={selected[key] ? 'all' : 'none'}
                            onClick={() => onToggle(key, buildMeta(sc))}
                        />
                        <span className="qb-tree-label qb-tree-label--subcategory">
                            {sc.subcategory_name}
                        </span>
                    </div>
                );
            })}
        </>
    );
};

/* ── Main tree component ────────────────────────────────────────── */
const QBTreeView = ({ onSelectionChange, selectedSubcategoryKeys = [] }) => {
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(() => {
        const map = {};
        selectedSubcategoryKeys.forEach(key => { map[key] = true; });
        return map;
    });

    useEffect(() => {
        ApiRequestService({ method: 'post', url: '/getAllSubjects' })
            .then(res => {
                setSubjects(
                    (res?.data?.data?.subject_names || []).filter(s => s.sync_validation)
                );
            })
            .finally(() => setLoading(false));
    }, []);

    // Notify parent once per state change
    useEffect(() => {
        if (onSelectionChange) {
            const keys = Object.keys(selected);
            const metadata = Object.entries(selected)
                .filter(([, v]) => v !== true)
                .map(([key, meta]) => ({ key, ...meta }));
            onSelectionChange(keys, metadata);
        }
    }, [selected]);

    const toggleSubcategory = useCallback((key, meta) => {
        setSelected(prev => {
            const next = { ...prev };
            if (next[key]) delete next[key];
            else next[key] = meta;
            return next;
        });
    }, []);

    // Replace `true` placeholders with real metadata once subcategories load
    const hydrateKeys = useCallback(items => {
        setSelected(prev => {
            let changed = false;
            const next = { ...prev };
            for (const { key, meta } of items) {
                if (next[key] === true) {
                    next[key] = meta;
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, []);

    const toggleAllSubcategories = useCallback(items => {
        setSelected(prev => {
            const next = { ...prev };
            const allSelected = items.every(({ key }) => next[key]);
            for (const { key, meta } of items) {
                if (allSelected) delete next[key];
                else next[key] = meta;
            }
            return next;
        });
    }, []);

    if (loading) {
        return (
            <div className="qb-tree-container">
                <div className="qb-tree-loading-text">Loading subjects...</div>
            </div>
        );
    }

    return (
        <div className="qb-tree-container">
            <div className="qb-tree-header">
                Select Categories &amp; Subcategories
            </div>
            <div className="qb-tree-body">
                {subjects.map(subj => (
                    <Branch
                        key={subj.subject_id}
                        label={subj.subject_name}
                        level={1}
                        levelClass="qb-tree-label--subject"
                        loadChildren={() =>
                            ApiRequestService({
                                method: 'post',
                                url: '/getAllTopics',
                                data: { subject_id: subj.subject_id },
                            }).then(res => res?.data?.data?.topic_names || [])
                        }
                        renderChildren={topics =>
                            topics.map(topic => (
                                <Branch
                                    key={topic.topic_id}
                                    label={topic.topic_name}
                                    level={2}
                                    levelClass="qb-tree-label--topic"
                                    loadChildren={() =>
                                        ApiRequestService({
                                            method: 'post',
                                            url: '/getAllCategories',
                                            data: {
                                                subject_id: subj.subject_id,
                                                topic_id: topic.topic_id,
                                            },
                                        }).then(res => res?.data?.data?.category_names || [])
                                    }
                                    renderChildren={categories =>
                                        categories.map(cat => (
                                            <CategoryNode
                                                key={cat.category_id}
                                                subj={subj}
                                                topic={topic}
                                                cat={cat}
                                                selected={selected}
                                                onToggle={toggleSubcategory}
                                                onToggleAll={toggleAllSubcategories}
                                                onHydrate={hydrateKeys}
                                            />
                                        ))
                                    }
                                />
                            ))
                        }
                    />
                ))}
            </div>
        </div>
    );
};

export default QBTreeView;
